import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { MIN_PASSWORD, canUseModule, normUser, usernameToAuthEmail } from '@aop/domain';
import { api, ApiError } from '../lib/api';
import { toCamel } from '../lib/case';
import { supabase } from '../lib/supabase';

export interface Me {
  id: string; username: string; fullName: string; email: string; phone: string; title: string;
  role: 'admin' | 'user'; modules: string[]; status: 'pending' | 'active' | 'inactive'; mustChange: boolean; lastLogin: string | null;
}

export type AuthState =
  | { phase: 'loading' }
  | { phase: 'setup' }
  | { phase: 'anon'; message?: string }
  | { phase: 'must-change'; me: Me }
  | { phase: 'ready'; me: Me };

interface AuthApi {
  state: AuthState;
  login(username: string, password: string): Promise<string>;                 // trả về thông báo lỗi, '' nếu thành công
  setupAdmin(b: { fullName: string; username: string; email: string; password: string; token?: string }): Promise<string>;
  setNewPassword(password: string): Promise<string>;
  logout(message?: string): Promise<void>;
  reloadMe(): Promise<void>;
  can(module: string): boolean;
}

const Ctx = createContext<AuthApi | null>(null);

const BAD_LOGIN = 'Tên đăng nhập hoặc mật khẩu không đúng.';
const NOT_ACTIVE = { pending: 'Tài khoản chưa được kích hoạt. Liên hệ quản trị.', inactive: 'Tài khoản đã ngừng kích hoạt. Liên hệ quản trị.' } as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ phase: 'loading' });
  const stateRef = useRef(state);
  stateRef.current = state;

  const fetchMe = useCallback(async (): Promise<Me | null> => {
    const { data, error } = await supabase().rpc('my_profile');
    if (error) throw error;
    return data && (data as { id?: string }).id ? toCamel<Me>(data as Record<string, unknown>) : null;
  }, []);

  /** Đọc hồ sơ và đưa app tới đúng màn hình. `fromLogin` = vừa gõ mật khẩu xong (ghi nhật ký đăng nhập). */
  const enter = useCallback(async (fromLogin: boolean): Promise<string> => {
    const me = await fetchMe();
    if (!me) { await supabase().auth.signOut(); setState({ phase: 'anon' }); return BAD_LOGIN; }
    if (me.status !== 'active') {
      await supabase().auth.signOut();
      setState({ phase: 'anon', message: NOT_ACTIVE[me.status] });
      return NOT_ACTIVE[me.status];
    }
    if (fromLogin) void supabase().rpc('record_login');
    setState(me.mustChange ? { phase: 'must-change', me } : { phase: 'ready', me });
    return '';
  }, [fetchMe]);

  const logout = useCallback(async (message?: string) => {
    setState({ phase: 'anon', message });
    await supabase().auth.signOut().catch(() => {});
  }, []);

  /* khởi động: có phiên thì vào thẳng, không thì xem đã có người dùng nào chưa */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await supabase().auth.getSession();
        if (data.session) { await enter(false); return; }
        const { data: need, error } = await supabase().rpc('bootstrap_needed');
        if (error) throw error;
        if (alive) setState(need ? { phase: 'setup' } : { phase: 'anon' });
      } catch {
        if (alive) setState({ phase: 'anon', message: 'Không kết nối được dữ liệu. Kiểm tra mạng hoặc cấu hình rồi tải lại trang.' });
      }
    })();
    const { data: sub } = supabase().auth.onAuthStateChange(ev => {
      if (ev === 'SIGNED_OUT') setState(s => (s.phase === 'anon' || s.phase === 'setup' ? s : { phase: 'anon' }));
    });
    return () => { alive = false; sub.subscription.unsubscribe(); };
  }, [enter]);

  /* theo dõi hồ sơ của chính mình: bị ngừng kích hoạt/xoá thì đăng xuất; đổi phân hệ thì cập nhật menu */
  const myId = state.phase === 'ready' || state.phase === 'must-change' ? state.me.id : null;
  const reloadMe = useCallback(async () => {
    try {
      const me = await fetchMe();
      if (!me) return void (await logout('Tài khoản của bạn đã bị xoá.'));
      if (me.status !== 'active') return void (await logout(NOT_ACTIVE[me.status]));
      setState(s => (s.phase === 'ready' ? { phase: 'ready', me } : s.phase === 'must-change' ? { phase: 'must-change', me } : s));
    } catch { /* mất mạng tạm thời: giữ nguyên */ }
  }, [fetchMe, logout]);

  useEffect(() => {
    if (!myId) return;
    const ch = supabase().channel('me-' + myId).on('postgres_changes', { event: '*', schema: 'public', table: 'profiles', filter: `id=eq.${myId}` }, () => { void reloadMe(); }).subscribe();
    const onVisible = () => { if (document.visibilityState === 'visible') void reloadMe(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { document.removeEventListener('visibilitychange', onVisible); void supabase().removeChannel(ch); };
  }, [myId, reloadMe]);

  const login = useCallback<AuthApi['login']>(async (username, password) => {
    const un = normUser(username);
    if (!un || !password) return BAD_LOGIN;
    const { error } = await supabase().auth.signInWithPassword({ email: usernameToAuthEmail(un), password });
    if (error) return /network|fetch/i.test(error.message) ? 'Không kết nối được máy chủ. Thử lại sau.' : BAD_LOGIN;
    try { return await enter(true); } catch { return 'Không đọc được hồ sơ người dùng. Thử lại sau.'; }
  }, [enter]);

  const setupAdmin = useCallback<AuthApi['setupAdmin']>(async ({ token, ...b }) => {
    try { await api.setup(b, token); } catch (e) { return e instanceof ApiError ? e.message : 'Không tạo được tài khoản.'; }
    return login(b.username, b.password);
  }, [login]);

  const setNewPassword = useCallback<AuthApi['setNewPassword']>(async password => {
    if (password.length < MIN_PASSWORD) return `Mật khẩu tối thiểu ${MIN_PASSWORD} ký tự.`;
    const { error } = await supabase().auth.updateUser({ password });
    if (error) return /same|khác|different/i.test(error.message) ? 'Mật khẩu mới phải khác mật khẩu tạm.' : 'Không lưu được, thử lại.';
    const id = stateRef.current.phase === 'must-change' ? stateRef.current.me.id : null;
    if (!id) return 'Phiên đã hết hạn, hãy đăng nhập lại.';
    const { error: e2 } = await supabase().from('profiles').update({ must_change: false }).eq('id', id);
    if (e2) return 'Không lưu được, thử lại.';
    return enter(false);
  }, [enter]);

  const value = useMemo<AuthApi>(() => ({
    state, login, setupAdmin, setNewPassword, logout, reloadMe,
    can: m => (state.phase === 'ready' ? canUseModule(state.me, m) : false),
  }), [state, login, setupAdmin, setNewPassword, logout, reloadMe]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth phải nằm trong <AuthProvider>');
  return v;
}

/** Người dùng đã đăng nhập (chỉ gọi trong phần ứng dụng sau khi vào được). */
export function useMe(): Me {
  const { state } = useAuth();
  if (state.phase !== 'ready' && state.phase !== 'must-change') throw new Error('Chưa đăng nhập');
  return state.me;
}
